// ============================================================================
// ClientPicker — choose the client a session belongs to. Searchable list with
// the avatar + name; starred clients float to the top. Wires to useClients.
// When the form is opened pre-scoped (?client=:id) the caller locks this to a
// read-only summary instead. Mobile-first big rows; reduced-motion aware.
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TextField, Avatar } from "../../components";
import { IconStar } from "../../components/icons";
import { useClients } from "../../hooks/clients";
import type { Client } from "../../api";
import { cn } from "../../lib/cn";

interface ClientPickerProps {
  value: number | null;
  onChange: (clientId: number, client: Client) => void;
  error?: string;
}

export function ClientPicker({ value, onChange, error }: ClientPickerProps) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const clientsQuery = useClients(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const list = (clientsQuery.data ?? []).filter((c) => c.status === "active");
    const q = query.trim().toLowerCase();
    const filtered = q
      ? list.filter((c) => c.name.toLowerCase().includes(q))
      : list;
    return [...filtered].sort((a, b) => {
      if (a.starred !== b.starred) return b.starred - a.starred;
      return a.name.localeCompare(b.name);
    });
  }, [clientsQuery.data, query]);

  return (
    <div className="space-y-3">
      <TextField
        label={t("form.searchClients")}
        hideLabel
        placeholder={t("form.searchClients")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputMode="search"
        error={error}
        iconLeft={
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
            <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        }
      />

      {clientsQuery.isLoading ? (
        <p className="py-6 text-center text-sm text-body-soft">
          {t("loading")}
        </p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-body-soft">
          {t("form.noClients")}
        </p>
      ) : (
        <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {rows.map((c, i) => {
            const active = value === c.id;
            return (
              <motion.li
                key={c.id}
                initial={reduce ? false : { opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.18,
                  delay: reduce ? 0 : Math.min(i * 0.02, 0.2),
                }}
              >
                <button
                  type="button"
                  onClick={() => onChange(c.id, c)}
                  aria-pressed={active}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md p-2.5 text-left ring-1 transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
                    active
                      ? "bg-coral-soft ring-coral/40"
                      : "bg-surface ring-line hover:-translate-y-0.5 hover:shadow-sm",
                  )}
                >
                  <Avatar name={c.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate font-display text-sm font-bold text-body">
                    {c.name}
                  </span>
                  {c.starred === 1 && (
                    <IconStar width={16} height={16} filled className="text-amber" />
                  )}
                  {active && (
                    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-coral" fill="none" aria-hidden>
                      <path d="m5 12 5 5 9-11" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </motion.li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
